// @ts-nocheck TODO

import {html, nothing} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {MobxLitElement} from "@adobe/lit-mobx";

import "../common/controls-inputs/Button.js";
import "../common/controls-inputs/Dropdown.js";
import "../common/controls-inputs/DropdownItem.js";
import "../common/controls-inputs/IconButton.js";
import "../common/controls-inputs/LabeledTextInput.js";
import {sidebarHeaderStyles} from "./SidebarHeader.styles.js";
import "./integration/IntegrationSignupModal.js";
import "./integration/IntegrationLoginModal.js";
import "./integration/IntegrationAccountModal.js";
import "./ProjectSelector.js";
import Data from "../../main/data/index.js";
import Session from "../../integrations/session/index.js";
import Highlighter from "../../integrations/highlighter/index.js";
import {attachModal, MODALS} from "../common/modals.js";
import Integration from "../../integrations/integration/index.js";
import {renderPowerNotesLogo} from "../common/powerNotesLogo.js";

@customElement("pn-sidebar-header")
export class SidebarHeader extends MobxLitElement {
	@property() isLoggedIn: boolean;
	@property({type: Boolean}) sessionCodes = true;
	@property({type: Boolean}) floating = false;
	@property({type: Boolean}) accountMenu = false;
	@property({type: Boolean}) projectSelector = true;
	@property({type: Boolean}) isHelpProjectVisible = false;

	@state() isSidebarHidden = false;
	@state() loginCode = "";

	static styles = [sidebarHeaderStyles];

	onClickHelp = () => {
		this.dispatchEvent(new CustomEvent("pnToggleHelpProject", {
			bubbles:  true,
			composed: true
		}));
	};

	onClickDisable = () => {
		this.dispatchEvent(new CustomEvent("pnDisable", {
			bubbles:  true,
			composed: true
		}));
		Integration.gtag("event", "disable");
	};

	onClickGetCode = () => {
		const myEvent = new CustomEvent("clickRestoreSession", {
			bubbles:  true,
			composed: true
		});

		this.dispatchEvent(myEvent);
	};

	onClickShowHide = () => {
		const newEvent = new CustomEvent("showHideSidebar", {
			bubbles:  true,
			composed: true
		});

		this.dispatchEvent(newEvent);

		this.isSidebarHidden = !this.isSidebarHidden;
	};

	onClickRestore = async () => {
		if (!Integration.isIntegration())
			throw new Error("Not an integration");

		if (!this.loginCode)
			return;

		const clientToken = await Integration.getClientToken();

		const {account, access_token, project} = await Data.app.backend.callApi({
			url:                   `integration/${Integration.name}/restore-session`,
			queryStringParameters: {client_token: clientToken},
			method:                "POST",
			body:                  {login_code: this.loginCode},
			isJsonBody:            true,
			expectedStatusCode:    200,
			expectedJson:          true,
			errorMessage:          "Error restoring your session."
		});

		Data.app.login(account.uuid, access_token);
		Data.app.setCurrentProject(project.uuid);
	};

	onClickLogIn = async () => {
		if (!Data.app.isLoggedIn && Integration.isIntegration())
			// TODO: this is not very efficient, but will do for now. We should just do a regular login in this case.
			await Data.app.createIntegrationAccount(Integration.name, Integration.getClientToken);

		attachModal(MODALS.INT_LOGIN);
	};

	onClickLogOut = () => {
		Integration.gtag("event", "logout");
		Data.app.logout();
		Session.clear();
		Highlighter.reinitIntegration();
		this.onClickShowHide(); // close the sidebar
	};

	onClickCreateAccount = async () => {
		if (!Data.app.isLoggedIn && Integration.isIntegration())
			await Data.app.createIntegrationAccount(Integration.name, Integration.getClientToken);

		attachModal(MODALS.INT_LOGIN);
	};

	onClickAccount = async () => {
		attachModal(MODALS.INT_ACCOUNT);
	};

	renderRestoreSession() {
		return html`
			<div id="restore-session-container">
				<pn-labeled-text-input
					.onInput=${e => {this.loginCode = e.target.value.trim();}}
					label="Session code"
					.value=${this.loginCode}
				></pn-labeled-text-input>
				<pn-button
					.onClick=${this.onClickRestore}
					renderType="purple-inverted"
				>Restore</pn-button>
			</div>
		`;
	}

	renderSaveSessionButton() {
		if (!this.isLoggedIn)
			return nothing;

		return html`
			<pn-button
				.onClick=${this.onClickGetCode}
				renderType="link"
			>Get Session Code</pn-button>
		`;
	}

	renderUpgradeAccountButton() {
		// TODO: put back later
		return nothing;

		// eslint-disable-next-line no-unreachable
		if (!this.isLoggedIn)
			return nothing;

		return html`
			<button>Upgrade to a Paid Plan</button>
		`;
	}

	renderAccountMenu() {
		if (!this.accountMenu)
			return nothing;

		const showCreateAccount: boolean = !Data.app.isLoggedIn || !Data.app.me?.email;
		const showLogIn: boolean = showCreateAccount;
		const showAccount: boolean = Data.app.isLoggedIn && Data.app.me?.email;
		const showLogout: boolean = showAccount;

		if (!showCreateAccount && !showLogIn && !showAccount && !showLogout)
			return nothing;

		return html`<div>
			<pn-dropdown
				icon="account"
				id="account-menu"
				.isAutoWidth=${true}
				.isDropdown=${true}
				.isRightAligned=${true}
				.tooltipOptions="${{
					position: "top",
					text:     "My Account"
				}}"
			>
				${showLogIn ? html`<pn-dropdown-item .onClick=${this.onClickLogIn} type="button">
					Log in
				</pn-dropdown-item>` : nothing}
				${showCreateAccount ? html`<pn-dropdown-item .onClick=${this.onClickCreateAccount} type="button">
					Create a PowerNotes Account
				</pn-dropdown-item>` : nothing}
				${showAccount ? html`<pn-dropdown-item .onClick=${this.onClickAccount} type="button">
					Account
				</pn-dropdown-item>` : nothing}
				${showLogout ? html`<pn-dropdown-item .onClick=${this.onClickLogOut} type="button">
					Log out
				</pn-dropdown-item>` : nothing}
			</pn-dropdown>
		</div>`;
	}

	renderProjectSelect() {
		if (!this.projectSelector || Data.project.all.length === 0)
			return nothing;

		if (this.isHelpProjectVisible) {
			return html`
				<div id="project-select-container">
					${renderPowerNotesLogo("pn-logo")}

					<div class="project-name">
						<span>Help: How to Use PowerNotes</span>
						<pn-button
							renderType="link-blue"
							@click=${this.onClickHelp}
						>&lt; Back to project</pn-button>
					</div>
				</div>
			`;
		}

		const isUnsaved = !Data.app.isLoggedIn || !Data.app.me?.email;

		return html`
			<div id="project-select-container">
				${renderPowerNotesLogo("pn-logo")}
	
				<pn-project-selector .isUnsaved=${isUnsaved}/>
			</div>
			${isUnsaved ? html`<div class="save-project-container">
				<pn-button
					.onClick=${this.onClickCreateAccount}
					renderType="auth"
				>
					Save Project
				</pn-button>
			</div>` : nothing}
		`;
	}

	renderHelpButton() {
		return html`<pn-icon-button
			icon="help"
			.onClick=${this.onClickHelp}
			.tooltipText=${"Help"}
		/>`;
	}

	renderDisableButton() {
		return html`<pn-icon-button
			icon="disable"
			.onClick=${this.onClickDisable}
			.tooltipText=${"Disable PowerNotes"}
		/>`;
	}

	renderCloseButton() {
		if (Integration.name !== "proquest")
			return nothing;

		return html`<pn-icon-button
			icon="close"
			.onClick=${this.onClickShowHide}
			.tooltipText=${"Close"}
		/>`;
	}

	renderMainHeader() {
		return html`<div id="main-header">
			<div id="header-buttons-group">
				${this.renderHelpButton()}
				${this.renderDisableButton()}
				${this.renderAccountMenu()}
				${this.renderCloseButton()}

				${!this.floating ? html`<pn-icon-button
					class="${classMap({
						rotated: this.isSidebarHidden
					})}"
					icon="round-alt-arrow-down"
					id="btn-show-hide"
					.onClick="${this.onClickShowHide}"
					tooltipText="Expand/Collapse Sidebar"
					tooltipPosition="top"
				></pn-icon-button>` : nothing}
			</div>
			${this.renderProjectSelect()}
		</div>`;
	}

	render() {
		if (!this.sessionCodes && !this.accountMenu && !this.projectSelector)
			return nothing;

		return html`
			<div id="main-container">
				${this.sessionCodes ? this.renderRestoreSession() : nothing}
				${this.sessionCodes ? this.renderSaveSessionButton() : nothing}
				${this.renderUpgradeAccountButton()}
				${this.renderMainHeader()}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-sidebar-header": SidebarHeader;
	}
}
